// src/utils/OpeningHoursGuard.tsx
import React, { useState, useEffect, ReactNode } from 'react';

// Constants for Mon-Fri
const MONDAY_TO_FRIDAY_OPENING_DAY_START = 1; // Monday
const MONDAY_TO_FRIDAY_OPENING_DAY_END = 5; // Friday
const MONDAY_TO_FRIDAY_OPENING_HOUR = 9; // 9 AM (09:00)
const MONDAY_TO_FRIDAY_CLOSING_HOUR = 18; // 6 PM (18:00) - Content shown up to 17:59:59

// Constants for Saturday
const SATURDAY_DAY = 6; // Saturday (getDay() returns 6 for Saturday)
const SATURDAY_OPENING_HOUR = 9; // 9 AM (09:00)
const SATURDAY_CLOSING_HOUR = 13; // 1 PM (13:00) - Content shown up to 12:59:59

const DEFAULT_HOLIDAY_JURISDICTION = 'england-and-wales';
const GOV_UK_BANK_HOLIDAYS_API = 'https://www.gov.uk/bank-holidays.json';

interface OpeningHoursGuardProps {
  children: ReactNode;
  jurisdiction?: 'england-and-wales' | 'scotland' | 'northern-ireland';
  /**
   * Determines visibility if the holiday API call fails.
   * true = show children (if within opening hours), false = hide children (default).
   */
  showOnApiError?: boolean;
  /** Optional: Component to display while checking opening hours / fetching holidays. */
  loadingComponent?: ReactNode;
}

const OpeningHoursGuard: React.FC<OpeningHoursGuardProps> = ({
  children,
  jurisdiction = DEFAULT_HOLIDAY_JURISDICTION,
  showOnApiError = true,
  loadingComponent = null,
}) => {
  const [isPermittedToShow, setIsPermittedToShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [apiErrorOccurred, setApiErrorOccurred] = useState(false);
  const [publicHolidays, setPublicHolidays] = useState<string[]>([]);

  useEffect(() => {
    const fetchHolidays = async () => {
      setIsLoading(true);
      setApiErrorOccurred(false);
      try {
        const response = await fetch(GOV_UK_BANK_HOLIDAYS_API);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const division = data[jurisdiction];

        if (division && Array.isArray(division.events)) {
          const holidayDates = division.events.map((event: any) => event.date);
          setPublicHolidays(holidayDates);
        } else {
          console.error(
            `OpeningHoursGuard: Holiday data for the specified jurisdiction ('${jurisdiction}') not found or in unexpected format.`
          );
          setPublicHolidays([]);
          setApiErrorOccurred(true);
        }
      } catch (error) {
        console.error(
          'OpeningHoursGuard: Could not fetch public holidays:',
          error
        );
        setPublicHolidays([]);
        setApiErrorOccurred(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHolidays();
  }, [jurisdiction]);

  useEffect(() => {
    const checkAndSetVisibility = () => {
      if (isLoading) {
        return;
      }

      if (apiErrorOccurred && !showOnApiError) {
        setIsPermittedToShow(false);
        return;
      }

      const now = new Date();
      let onPublicHoliday = false;

      if (!apiErrorOccurred || (apiErrorOccurred && showOnApiError)) {
        if (publicHolidays.length > 0) {
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const formattedDate = `${year}-${month}-${day}`;
          if (publicHolidays.includes(formattedDate)) {
            onPublicHoliday = true;
          }
        }
      }

      if (onPublicHoliday) {
        setIsPermittedToShow(false);
        return;
      }

      const currentDay = now.getDay(); // Sunday = 0, Monday = 1, ..., Saturday = 6
      const currentHour = now.getHours();
      let isCurrentlyOpen = false;

      // Check for Monday to Friday
      if (
        currentDay >= MONDAY_TO_FRIDAY_OPENING_DAY_START &&
        currentDay <= MONDAY_TO_FRIDAY_OPENING_DAY_END
      ) {
        if (
          currentHour >= MONDAY_TO_FRIDAY_OPENING_HOUR &&
          currentHour < MONDAY_TO_FRIDAY_CLOSING_HOUR // e.g. 9:00 up to 17:59:59
        ) {
          isCurrentlyOpen = true;
        }
      }
      // Check for Saturday
      else if (currentDay === SATURDAY_DAY) {
        if (
          currentHour >= SATURDAY_OPENING_HOUR &&
          currentHour < SATURDAY_CLOSING_HOUR // e.g. 9:00 up to 12:59:59
        ) {
          isCurrentlyOpen = true;
        }
      }

      setIsPermittedToShow(isCurrentlyOpen);
    };

    checkAndSetVisibility();
    const intervalId = setInterval(checkAndSetVisibility, 60000);

    return () => clearInterval(intervalId);
  }, [
    isLoading,
    apiErrorOccurred,
    publicHolidays,
    showOnApiError,
    jurisdiction, // Keep jurisdiction here as it influences publicHolidays
  ]);

  if (isLoading) {
    return loadingComponent;
  }

  if (isPermittedToShow) {
    return <>{children}</>;
  }

  return null;
};

export default OpeningHoursGuard;
